// src/pages/Accounts/SupplierPaymentList.js
import React, { useEffect, useState } from "react";

import { Table, Button, Row, Col, Typography, Tag, App,  Tooltip, Drawer, Grid} from "antd";

const { useBreakpoint } = Grid;
import { ArrowLeftOutlined, EditOutlined, PlusOutlined,   QuestionCircleOutlined, FileOutlined} from "@ant-design/icons";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayments, deletePayment } from "./supplierPaymentThunk";
import {
  resetPayments,
  setSelectedPayment,
  setIsEditPayment,
  setRefreshList,
} from "./supplierPaymentSlice";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import {
  formatDateOnly,
  formatToCompanyTimezone,
  getAccessType,
} from "../../lib/functions";
import { DeleteOutlined } from "@ant-design/icons";
import { useIsAdminUser } from "../../lib/accessList";
import { useTxLock } from "../../hooks/useTxLock";

const format = commaNumber.bindWith(",", ".");

const SupplierPaymentList = () => {
  const { modal } = App.useApp();
  const { Title } = Typography;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { payments, loading, refreshList } = useSelector(
    (state) => state.supplierPayments,
  );

  const isAdminUser = useIsAdminUser();
  const screens = useBreakpoint();
   const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const access = getAccessType("creditor_payment");
    access_type_set(access);
  }, []);

  useEffect(() => {
    dispatch(resetPayments());
    dispatch(fetchPayments());
  }, [dispatch]);

  // auto-refresh if flagged (after save)
  useEffect(() => {
    if (refreshList) {
      dispatch(fetchPayments());
      dispatch(setRefreshList(false));
    }
  }, [refreshList, dispatch]);

  const handleDelete = async (pay_id) => {
    try {
      await dispatch(deletePayment(pay_id)).unwrap();
      modal.success({
        title: "Suppliier Payment entry deleted successfully",
      });
      dispatch(fetchPayments());
    } catch (error) {
      modal.warning({
        title: error || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Payment Code",
      dataIndex: "pay_code",
    },
    {
      title: "Payment Date",
      dataIndex: "created_date",
      render: (value) => (value ? formatDateOnly(value) : "-"),
    },
    {
      title: "Supplier",
      render: (_, record) =>
        record.details && record.details.length > 0
          ? record.details[0].to_acc_name || record.acc_name || ""
          : record.acc_name || "",
    },
    {
      title: "Total Discount",
      dataIndex: "discount_total",
      align: "right",
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: "Paid Total",
      dataIndex: "pay_total",
      align: "right",
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: "Description",
      dataIndex: "narration",
    },
    {
      title: "Created By",
      dataIndex: "full_name",
    },
 {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <>
        <Button
          type="link"
          icon={<FileOutlined />}
          onClick={() =>
            navigate(`/accounts/creditor-payments/view/${record.pay_id}`)
          }
        />

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            disabled={!(isAdminUser || canEdit) || txLocked}
            onClick={() => {
              if (txLocked) return;

              dispatch(setSelectedPayment(record));
              dispatch(setIsEditPayment(true));

              navigate(
                `/accounts/creditor-payments/edit/${record.pay_id}`
              );
            }}
            icon={<EditOutlined />}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Button
            type="link"
            danger
            disabled={!(isAdminUser || canDelete) || txLocked}
            icon={<DeleteOutlined />}
            onClick={() => {
              if (txLocked) return;

              modal.confirm({
                title: "Are you sure?",
                content: `This will delete contra entry with amount: ${record.pay_total}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () => handleDelete(record.pay_id),
              });
            }}
          />
        </Tooltip>
      </>
    );
  },
}
  ];

  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "From Account", dataIndex: "from_acc_name" },
    {
      title: "Amount",
      dataIndex: "pay_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },

    {
      title: "Status",
      dataIndex: "payment_status",
      align: "center",
      render: (val) => {
        const color =
          val === "approved" ? "green" : val === "rejected" ? "red" : "gold";
        return (
          <Tag color={color}>{val.charAt(0).toUpperCase() + val.slice(1)}</Tag>
        );
      },
    },
  ];

  return (
    <>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "12px",
      }}
    >
      <Button
        type="text"
        icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
        onClick={() => navigate(-1)}
        data-testid="supplier-payments-back-button"
      />
      <Title
        level={3}
        style={{
          margin: 0,
          wordBreak: "break-word",
          fontSize: screens.md ? undefined : "18px",
        }}
      >
        Supplier / Customer Payments
      </Title>

      <div style={{ marginLeft: "auto" }}>
        <Tooltip title="Help">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
            onClick={() => setHelpOpen(true)}
          />
        </Tooltip>
      </div>
    </div>
      <Row justify="end" style={{ marginBottom: 32, marginTop: 32 }}>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                data-testid="btn-add-payment"
                onClick={() => {
                  dispatch(setSelectedPayment(null));
                  dispatch(setIsEditPayment(false));
                  navigate("/accounts/creditor-payments/add");
                }}
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={payments}
        rowKey="pay_id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: payments.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
        size="middle"
        scroll={{ x: "max-content" }}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <Table
                rowKey="detailId"
                columns={detailColumns}
                dataSource={record.details || []}
                pagination={false}
                size="small"
                bordered
              />
            );
          },
        }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
      <Drawer
        title="Supplier Payment Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Supplier / Customer Payment Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Supplier / Customer Payment module is used to record and manage
            outgoing payment transactions within the ERP accounting system.
            It helps track supplier payments, customer settlements,
            discounts, approvals, and financial reporting.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View supplier and customer payment records</li>
              <li>Use <strong>Add New</strong> to create payment entries</li>
              <li>Edit payment details when required</li>
              <li>Delete payment entries if permitted</li>
              <li>Track payment totals and discounts</li>
              <li>Monitor payment approval statuses</li>
              <li>Review detailed account allocations using expandable rows</li>
              <li>Track payment narration and transaction history</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Payment entries affect accounting balances immediately.</li>
              <li>Status indicates whether payments are approved or rejected.</li>
              <li>Expandable rows display detailed payment account information.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Transaction history is maintained for auditing and reporting.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default SupplierPaymentList;
